//This file contains shared functions between desktop & mobile
//Reduces copy-pasting on most parts

import React from "react";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css
import "../customOverlay.scss";

import screenfull from "screenfull";
import { isIPhone } from "./iOSDetection";

//MACHINE GAMES
import { fillEmptyUnsubmittedTickets } from "./commonMachineGame";

//Lotto
import {
	applyM26SubmitTickets,
} from "./games/LottoGame";

//Shio Balls
import {
	applyM29SubmitTickets,
} from "./games/ShioBalls";

//5D
import {
	applyM30SubmitTickets,
} from "./games/5D";
//END OF MACHINE GAMES

//Component imports
import MobileGame from "@/components/MobileGame";
import Desktop from "@/components/Desktop";
import playTTS from "@/components/TTS/playTTS";
import MobileLobby from "@/components/MobileLobby";

// Betting table elements
import Chips from "../components/Chips/";
import Tooltip from "../components/Tooltip/";

// Module Imports
import getRoadmapText from "./getRoadmapText";


//Observer script
import smartlookClient from "smartlook-client";
import { identify } from "react-fullstory";


// Type Imports
import {
	IGameType,
	ILanguageType,
	IGameObjectType,
	IUserdataType,
	ITimerType,
	IKeyAnyDict,
	ILastBetType,
	IKeyNumberDict,
	IBetHistory,
	IGameTableType,
	IGongballResult,
	ILastBetArrayType,
	ITotoGameTableType,
	IGameTypeM42,
} from "@/types/GlobalTypes";
import { IRoadmapSmallProps, IRoadmapSmallClass } from "@/types/RoadmapSmallTypes";
import { IRoadmapLargeProps } from "@/types/RoadmapLargeTypes";
import { IGameResultHistoryProps } from "@/types/GameHistoryTypes";
import { ILongerGameStatisticsProps } from "@/types/LongerStatisticsTypes";
import { IPopupResultHistoryProps } from "@/types/PopupResultHistoryTypes";
import { loadLanguage } from "./locales";

import { compareSamGongResult, SamGongWinners } from "./games/SamGong";
import { defaultGameNames, getBasePcode, getClonePcode, getGameVariantLetter, isMachineGame, isTotoGame, newGetPcode } from "./gamePcode";
import { defaultDragonTigerResult, defaultShioFightResult, genericCards } from "./virtualCards";
import { applyM42SubmitTickets } from "./games/Pinball";
import MobileDetect from "mobile-detect";

const defaultLogo = "/assets/img/logo.png";

// Used in chip glows
const chipColours = [
	"blue", "green", "red", "yellow",
	"orange", "black", "purple", "white",
	"brown", "dark-blue"
];

// show type in Popup result history
const showTypeResultHistory: string[] = ["m17"];

function isMobile() {
	return !!(new MobileDetect(window.navigator.userAgent)).mobile();
}

/**
 * Process time for Toto games.
 * @param time time in seconds
 * @returns time string (h:mm:ss)
 */
function timeLeftStrToto(time: number) {
	const pad2 = (num: number) =>
		num.toFixed(0).padStart(2, "0");

	const h = Math.floor(time / 3600),
		m = pad2(time % 3600 / 60),
		s = pad2(time % 3600 % 60);

	return `${h}:${m}:${s}`;
}

//Roadmap & History toggle on Desktop & Mobile Lobby
const historyOnlyGames = ["m7", "m17", "m19", "m26", "m29", "m30", "m33", "m39", "m42", "m50", "m51"];

function isHistoryOnlyGame(pcode: string) {
	const basePcode = getBasePcode(pcode);
	return historyOnlyGames.includes(basePcode);
}

// const skipRoadmapSmall: string[] = ["m17"];
const roadmapOnlyGames = ["m24"];
const noStatisticsGames = ["m19", "m24"];


//Single game large chips
const bigChipGames = ["m8", "m14", "m16", "m10", "m20", "m13", "m11"];

function isBigChipGame(pcode: string) {
	const basePcode = getBasePcode(pcode);
	return bigChipGames.includes(basePcode);
}


function getDefaultGongBallResult(): IGongballResult {
	return { player: "", banker: "" };
}

// const defaultGongBallResult = { player: "6,7,8", banker: "J,Q,K" };

export interface IDragonTigerWinType {
	dragon: genericCards,
	dragonValue: number,
	tiger: genericCards,
	tigerValue: number
}


//Common Patterns on V1 games
const rouletteReds = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36],
	twelveDReds = [1, 2, 3, 7, 8, 9],
	twentyFourDReds = [1, 2, 3, 4, 9, 10, 11, 12, 17, 18, 19, 20],
	fortyEightDReds = [...twentyFourDReds, 25, 26, 27, 28, 33, 34, 35, 36, 41, 42, 43, 44];

const headTailLefts = ["1,1,1", "1,2,2", "1,1,2", "1,2,1"];

const monopolyCircles = ["indonesia", "malaysia", "japan", "china", "macao", "india", "philippines", "laos"];
const monopolySquares = ["singapore", "brunei", "korea", "hongkong", "cambodia", "taiwan", "vietnam", "thailand"];

const shioFightsAnimals = ["rat", "ox", "tiger", "rabbit", "dragon", "snake", "horse", "goat", "monkey", "rooster", "dog", "pig"];

/**
 * Display a simple warning whether temporary data is on or enable timers is off.
 * @param mainClass 
 */
function devWarnings(mainClass: Desktop | MobileGame | MobileLobby) {
	if (mainClass.state.useTempData)
		console.warn("useTempData is on, will use temporary data for debugging.");

	if (!mainClass.state.enableTimers)
		console.warn("enableTimers is off, the timers won't start.");

}

/**
 * string.prototype.indexOf() but with regex.
 * Reference:
 *   https://stackoverflow.com/questions/273789/is-there-a-version-of-javascripts-string-indexof-that-allows-for-regular-expr
 * @param {string} str      The string reference.
 * @param {RegExp} regex    Regular Expression object to be used in the search.
 * @param {number} startpos Starting position of the string to check.
 */
function regexIndexOf(str: string, regex: RegExp, startpos: number = 0): number {
	const indexOf = str.substring(startpos || 0).search(regex);
	return (indexOf >= 0) ? (indexOf + (startpos || 0)) : indexOf;
}


//Groups with 50:50 bet chances
const ary50 = [
	//Most common 50:50
	"n50",

	"small", "big", "odd", "even", "red", "black",

	// Baccarat
	"player", "banker",

	// DT
	"dragon", "tiger",
];

function checkAllBetBounds(game: IGameType) {
	// Object.keys(game.bet).forEach(betKey => {
	for (const betKey in game.bet) {
		const split = betKey.split("+");
		const group = split[1];
		checkBetBounds(game, group, betKey);
	}
	// });
}

//Summary:
// Checks bet bounds according to min - max parameters in userdata
//Parameters:
// game: the game object from fetchGameList()
//in any Games\*\index.js parameters:
// group: bet group
// key: bet key
function checkBetBounds(game: IGameType, group: string, betKey: string): void {
	const is50 = ary50.indexOf(group.toLowerCase()) >= 0;

	game.betOutOfBounds[betKey] = (is50) ?
		(game.bet[betKey] < game.min50 || game.bet[betKey] > game.max50)
		: (game.bet[betKey] < game.min || game.bet[betKey] > game.max);
}


// function checkUnsubmittedTicketBetBounds(game: IGameType, unsubmittedTicketIndex: number): boolean {
// 	const betAmount = game.unsubmittedTickets[unsubmittedTicketIndex].bet;
// 	return (betAmount < game.min || betAmount > game.max);
// }

// Gets last bet array from userdata
function getLastBetArray(userdata: IUserdataType, pcode: string): ILastBetArrayType[] {
	const filtered = userdata.user.lastBet.filter((item: any) =>
		item.game === pcode
	) as ILastBetType[];

	if (filtered.length > 0)
		return filtered[0].lastBet;
	return [];
}

// for use in file names
function getDefaultGameName(pcode: string, ingroup = false) {
	const basePcode = getBasePcode(pcode);

	if (ingroup) //Example expected output: "Baccarat B"
		return `${defaultGameNames[basePcode]} ${getGameVariantLetter(pcode)}`;

	return defaultGameNames[basePcode];
}

// For use in game header, lobby desktop name and every game name
// that the user sees.
//
// Return special game name if it's included in the translation
// Example:
//  "txt_game_name_p7b"
//  > "Roulette 2 - Fast Table"
function getTranslatedGameName(langdata: ILanguageType, pcode: string, ingroup = false, hasgroup = false) {
	if (hasgroup) {
		if (isTotoGame(pcode)) {
			return langdata["txt_game_name_toto"];
		}
	}

	// Find the exact game name
	const txtKey = `txt_game_name_${pcode}`;
	if (txtKey in langdata)
		return langdata[txtKey];

	// In case there's no exact game name
	const basePcode = getBasePcode(pcode);
	const key = `txt_game_name_${basePcode}`;

	return (
		ingroup
			? `${langdata[key]} ${getGameVariantLetter(pcode)}`
			: defaultGameNames[basePcode]
	);
}


function getTimeText(timerData: ITimerType): number {
	let secondsLeft = timerData ? timerData.time : 0;

	if (!isNaN(secondsLeft))
		secondsLeft = Number(secondsLeft) > 0
			? Number(secondsLeft)
			: 0;

	return secondsLeft;
}

/**
 * Checks if the game is closed or not
 */
const isBetClosed = (secondsLeft: number) =>
	secondsLeft <= 0 ||
	isNaN(secondsLeft);


/**
 * to check whether the window is opened from another window, for use in iframe
 */
const getWindowOpener = () => window.opener;


/**
 * Get win class name.
 * Used in components under Components\Games directory.
 * @param bool the condition to check
 * @returns " sl-yellow-alternating" if true, empty string is false.
 */
const getWinClassName = (statement: boolean) =>
	statement
		? " sl-yellow-alternating"
		: "";


/**
 * Checks whether this web app runs on localhost or dev domains.
 */
const isDev = () =>
	["localhost", "dev"].some(domain =>
		window.location.href.includes(domain)
	);


/* Component Imports */
function Table(p: IGameTableType) {
	// console.log("Table props ", p);
	// console.log("p data:", p.data);

	if (!p.pcode) {
		console.error("Table has no pcode, returning null");
		return null;
	}

	const pcode = p.pcode;
	const basePcode = getBasePcode(pcode);
	const clonePcode = getClonePcode(basePcode);

	let Component = null;
	let output = null;

	try {
		Component = require("../components/Games/" + clonePcode).default as React.Component<IGameTableType>;
		// const Component = createLazyContainer(() => import("../components/Games/" + clonePcode));
		output = <Component
			pcode={pcode}
			bets={p.data.bet}

			//Bounds checks
			betOutOfBounds={p.data.betOutOfBounds}

			win={p.data.win}

			maximised={p.maximised || p.data.maximised || false}

			//Machine Games
			mustSubmitTickets={p.data.mustSubmitTickets}
			activeBetGroup={p.data.activeBetGroup}

			groupedUnsubmittedTickets={p.data.groupedUnsubmittedTickets}
			unsubmittedTickets={p.data.unsubmittedTickets}

			activeUnsubmittedTicketRow={p.data.activeUnsubmittedTicketRow}

			{...p}
		/>;
	} catch (ex) {
		console.error(ex);
	}

	return output;
}

function TotoTable(p: ITotoGameTableType) {
	if (!p.pcode) {
		console.error("Table has no pcode, returning null");
		return null;
	}

	const pcode = p.pcode;
	const basePcode = getBasePcode(pcode);
	const clonePcode = getClonePcode(basePcode);

	let Component = null;
	let output = null;

	try {
		Component = require("../components/Games/" + clonePcode).default as React.Component<IGameTableType>;
		// const Component = createLazyContainer(() => import("../components/Games/" + clonePcode));
		output = <Component
			{...p}
		/>;
	} catch (ex) {
		console.error(ex);
	}

	return output;
}

function RoadmapLarge(p: IRoadmapLargeProps) {
	const basePcode = getBasePcode(p.pcode);
	const clonePcode = getClonePcode(basePcode);

	if (historyOnlyGames.indexOf(basePcode) >= 0)
		return null;

	let Component = null;
	let output = null;

	try {
		Component = require("../components/RoadmapLarge/" + clonePcode).default;
		output = <Component {...p} />;
	} catch (ex) {
		console.error(ex);
	}

	return output;
}


function RoadmapSmall(p: IRoadmapSmallProps) {
	const basePcode = getBasePcode(p.pcode),
		clonePcode = getClonePcode(basePcode);

	if (historyOnlyGames.indexOf(basePcode) >= 0)
		return null;

	// if (skipRoadmapSmall.indexOf(basePcode) >= 0)
	// 	return null;

	let Component = null;
	let output = null;
	try {
		Component = require("../components/RoadmapSmall/" + clonePcode).default as IRoadmapSmallClass;
		output = <Component {...p} />;
	} catch (ex) {
		if (isDev())
			console.error(ex);
	}

	return output;
}

function History(p: IGameResultHistoryProps) {
	const basePcode = getBasePcode(p.pcode);
	const clonePcode = getClonePcode(basePcode);

	if (roadmapOnlyGames.indexOf(basePcode) >= 0)
		return null;

	let output = null;

	try {
		const Component = require("../components/GameResultHistory/" + clonePcode).default;
		output = <Component {...p} />;
	} catch (ex) {
		if (isDev())
			console.error(ex);
	}

	return output;
}

function GameStatistics(p: ILongerGameStatisticsProps) {
	const basePcode = getBasePcode(p.pcode);
	const clonePcode = getClonePcode(basePcode);

	if (noStatisticsGames.indexOf(basePcode) >= 0)
		return null;

	let Component = null;
	let output = null;

	try {
		Component = require("../components/GameStatistics/" + clonePcode).default;
		output = <Component {...p} />;
	} catch (ex) {
		if (isDev())
			console.error(ex);
	}

	return output;
}


/**
 * Mobile & Desktop Result History
 * @param p 
 */
function PopupResultHistory(p: IPopupResultHistoryProps) {
	const basePcode = getBasePcode(p.activeGame.pcode);
	const clonePcode = getClonePcode(basePcode);

	let Component = null, output = null;

	try {
		Component = require("../components/PopupResultHistory/" + clonePcode).default;
		output = <Component {...p} />;
	} catch (ex) {
		console.error(ex);
	}

	return output;
}

function StandardBetSB(props: any) {
	const commonProps = props.commonProps;
	const betKey = props.betNum + "+" + props.betGroup;

	const customAttributes = {
		bet: props.betNum,
		group: props.betGroup,
		pcode: commonProps.pcode
	};

	return (
		<button className={"sl-bet " + props.classes}
			key={props.key || (commonProps.pcode + "_" + betKey)}

			{...customAttributes}

			disabled={props.disabled}
			onClick={props.onClick}
		>
			{
				props.isTopRow ?
					<img className="left-wing" src="/assets/img/tab_wing_left.png" alt="" />
					: null
			}

			<div className="bet-type">
				{props.betType}
			</div>

			<div className="bet-label">
				{props.betLabel}
			</div>

			{
				commonProps.bets ?
					<Chips
						mobile={commonProps.mobile}
						betOutOfBounds={commonProps.betOutOfBounds}
						chipBase={commonProps.chipBase}
						bets={commonProps.bets}
						betKey={betKey} />
					: null
			}

			{
				//Ticket Count (Single Game Desktop)
				commonProps.ticketCounts &&
					commonProps.ticketCounts[props.betGroup] ?
					<div className="ticket-count">
						{`${commonProps.ticketCounts[props.betGroup]} TICKET(S)`}
					</div> : null
			}

			{
				commonProps.bets ?
					<Tooltip bet={commonProps.bets[betKey]} langStr="en" />
					: null
			}

			{
				props.isTopRow ?
					<img className="right-wing" src="/assets/img/tab_wing_right.png" alt="" />
					: null
			}
		</button>
	);
}



/**
 * Set active game for result history popup.
 * @param e 
 */
function setActiveGame(e: React.MouseEvent) {
	const mainClass = this;
	const pcode = e.currentTarget.getAttribute("game");

	mainClass.activePcode = pcode;

	//Fetch result history again
	mainClass.setState({
		activeGame: mainClass.state.games[pcode]
	}, function () {
		mainClass.fetchResultHistory();
	});
}


/**
 * Initialise userdata and performs conversions to comply the typings.
 * @param userdata 
 */
function initUserdata(userdata: IUserdataType) {
	//Strips nickname from user agent for display
	userdata.user.displayNickname = getNickname(userdata.user.nickname);

	// Converts user balance to number
	userdata.user.balance = Number(userdata.user.balance);

	// Converts chips into numbers
	userdata.user.chipBase = userdata.user.chipBase.map((item: any) => Number(item));


	// Loads language config from localStorage
	loadLanguage(userdata);
}

/**
 * Get the nickname after trimming the operator agent string
 * @returns Nickname without operator agent name
 * @param nickname 
 */
const getNickname = (nickname: string) =>
	nickname.split(/(-|_)/).slice(-1).join("");


//V1 Legacy
//Template:
//  {...betGroupProp(this, "", "")}

//Example:
//<div
//  {...betGroupProp(this, "Small", "n50")}
//>Bet</div>
const betGroupProp = (mainClass: any, betKey: string, betGroup: string) => (
	{
		bet: betKey,
		group: betGroup,
		pcode: mainClass.props.pcode
	}
);


//Gets URL from game name
function replaceGameNameWithURL(pcodeOrName: string, gameArray?: IGameObjectType) {
	// console.log("rGNWU", pcodeOrName);
	console.log("pcodeOrName")
	console.log(pcodeOrName)
	const gameObject = gameArray ?
		gameArray[pcodeOrName] :
		(this ? this.state.games[pcodeOrName] : null);

	const gameName: string = gameObject ?
		(gameObject.name || "").toLowerCase() :
		pcodeOrName.toLowerCase();

	if (gameName.includes("duel-dice"))
		return "dueldice";

	return gameName.indexOf("baccarat") >= 0 ||
		gameName.indexOf("dragon") >= 0 ||
		gameName.indexOf("roulette") >= 0 ?
		gameName.replace("[", "-").replace(/(]| )/, "") :
		gameName.replace(/( |\[)/g, "-").replace("]", "");
}


/**
 * (Unused) Scroll event for overflowing roadmaps
 * @param e 
 */
function scrollStart(e: React.MouseEvent) {
	const increment = Number(e.currentTarget.getAttribute("increment"));
	const direction = e.currentTarget.getAttribute("direction");

	// console.log(this.targetContainer);
	if (this.targetContainer === null)
		return;
	// console.log(this.targetContainer.scrollTop, increment);

	this.scrollInterval = setInterval(component => {
		switch (direction) {
			case "horizontal":
				component.scroll(component.scrollLeft + 10 * increment, 0);
				break;
			case "vertical":
				component.scroll(0, component.scrollTop + 5 * increment);
				break;
			default: break;
		}
	}, 20, this.targetContainer);
}

/**
 * (Unused)
 */
function scrollEnd() {
	window.clearInterval(this.scrollInterval);
}

const trimLastSlash = (href: string) =>
	href[href.length - 1] === "/"
		? href.substr(0, -1)
		: href;

/**
 * Checks if the device is running inside an <iframe> tag
 */
function inIframe(): boolean {
	try {
		return window.self !== window.top;
	} catch (e) {
		return true;
	}
}

//Checks if the device is running Chrome on desktop or mobile
//https://stackoverflow.com/questions/4565112/javascript-how-to-find-out-if-the-user-browser-is-chrome
function isChrome(): boolean {
	return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
}

//Checks if the iOS is running Chrome
function isChromeiOS(): boolean {
	return /CriOS/.test(navigator.userAgent);
}

//https://stackoverflow.com/questions/384286/javascript-isdom-how-do-you-check-if-a-javascript-object-is-a-dom-object
//Returns true if it is a DOM element
//Parameters:
//o: Object -- can be HTML Element or HTML Node
function isElement(o: any): boolean {
	return (
		typeof HTMLElement === "object" ? o instanceof HTMLElement : //DOM2
			o &&
			typeof o === "object" &&
			o !== null &&
			o.nodeType === 1 &&
			typeof o.nodeName === "string"
	);
}

//Checks if the device is running Firefox
function isFirefox(): boolean {
	return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
}

//Checks if the device orientation is portrait
function isLandscape(): boolean {
	return isIPhone() ?
		Math.abs(+window.orientation) === 90 :
		inIframe() ? window.innerWidth > window.innerHeight :
			window.screen.width > window.screen.height;
}

//Checks if the device orientation is portrait
function isPortrait(): boolean {
	return isIPhone() ?
		Math.abs(+window.orientation) !== 90 :
		inIframe() ? window.innerHeight > window.innerWidth :
			window.screen.height > window.screen.width;
}


//Calculates the maximum scrollable pixels in a DOM
function maxScrollLeft(dom: any): void {
	if (dom)
		dom.scrollLeft = dom.scrollWidth - dom.clientWidth;
}

/**
 * Check user idle status
 * Used in fetchUserData, fetchGameList and fetchGameTime
 */
function checkFetchStatus(response: any): void {
	if (!response) {
		console.log('error get data')
		window.location.assign("https://page.idnlive.club/error.html");
		return;
	}

	if (response.status === false)
		// window.location.assign("/errors/idle");
		window.location.assign("https://page.idnlive.club/idle.html");
}




//Fetches favourited games
function sortNewGames(gameArray: IGameObjectType) {
	// BEGIN SORTING
	const oldGames = JSON.parse(JSON.stringify(gameArray)) as IGameObjectType;

	//array of new gameArray, reversed
	const newGames = Object.keys(gameArray).map((item: string) =>
		gameArray[item].newgame === "1" ? item : null
	).filter(item => item).reverse().map(item => gameArray[item]) as IGameType[];

	let p: string;

	//p: pCode
	for (p in oldGames)
		if (p in newGames)
			delete oldGames[p];

	const sortedGames: IKeyAnyDict = {};

	for (p in newGames)
		sortedGames[newGames[p].pcode] = newGames[p];

	for (p in oldGames)
		sortedGames[p] = oldGames[p];

	return sortedGames;
}

//Fills Roadmap Text
function fillRoadmapText(games: IGameObjectType, langdata: ILanguageType) {
	Object.keys(games).forEach(pcode => {
		games[pcode].roadmapText = {};

		games[pcode].roadmapFilters.forEach(filter => {
			games[pcode].roadmapText[filter] = getRoadmapText(pcode, filter, langdata);
		});
	});
}


/**
 * Toggle fullscreen (only for mobile).
 */
function toggleFullscreen(): void {
	const ele: HTMLElement = document && document.getElementById("loadingOverlay");

	// Prevents fullscreen on loading
	if (ele && ele.style.display !== "none") {
		console.log("Please wait until loadingOverlay is invisible");

		return;
	}

	const mobileGame = this;

	if (screenfull.enabled) {
		// if (screenfull.isEnabled) {
		screenfull.toggle();
		// screenfull.request();

		mobileGame.setState({ isFullscreen: true });
	}
}

// As per 17-10-2018, all temp data is moved to local Apache server
export const
	tempDataPage = "http://localhost/seamlessTempData/tempdata.php",
	tempDataDir = "http://localhost/seamlessTempData/";

/**
 * Fetch game history from the server.
 * Updates: state.games.history, state.games.roadmap.
 */
function fetchGameHistory(this: Desktop | MobileLobby | MobileGame) {
	const mainClass = this;
	const { games, useTempData } = this.state;

	const request = (useTempData) ?
		new Request(tempDataPage + "?page=game-history") :
		new Request("/games/history");

	let init: RequestInit;
	if (!useTempData)
		init = { credentials: "same-origin" };

	//Fetch game list
	fetch(request, init)
		.then(res => res.json())
		.then(gameHistoryResponse => {
			checkFetchStatus(gameHistoryResponse);

			const gamePcodes = Object.keys(gameHistoryResponse);

			for (const pcode of gamePcodes) {
				const
					game = games[pcode],
					basePcode = getBasePcode(pcode),
					gameHistory = gameHistoryResponse[pcode],
					{ roadmap, history } = gameHistory;

				// skip missing games
				if (!game) {
					// console.log("Skipped game " + pcode);
					continue;
				}

				if (basePcode !== "m24") {
					//Skip history reversal for m24
					if (roadmap && roadmap.big_road)
						roadmap.big_road.reverse();

					if (history)
						history.reverse();
				}

				game.history = history;
				game.roadmap = roadmap;
			}

			(mainClass as any).setState({ games });
		})
		.catch(error =>
			// alert("Failed to fetch game history, please try again later.");
			console.error("Failed to fetch game history", error)
		);
}

// END COMMON LOBBY FUNCTIONS


// Common loadNewValue functions
// Use gameResult to show the animation.
const skipLoadNewValuePcodes = ["p7", "p6", "p9", "p12", "m8", "m14", "m37"];

/**
 * Process loadNewValue socket response.
 * @param mainClass 	   Either Lobby or MaximizeMobileGame, untyped because both classes don't have the same state.
 * @param loadNewValueData The socket response of "loadNewValue" or "gameResult".
 */
function processLoadNewValueWin(mainClass: any,
	loadNewValueData: any) {

	const isDesktop = mainClass instanceof Desktop;
	const mobile = isMobile();// mainClass instanceof MobileGame;

	const { games } = mainClass.state;

	const pcode = newGetPcode(loadNewValueData);
	const basePcode = getBasePcode(pcode);

	// if (isDev() &&
	if (skipLoadNewValuePcodes.indexOf(basePcode) >= 0)
		return;

	const activeGame = games[pcode];

	if (!activeGame)
		return;

	//Example loadNewValue data (from 24D)
	// {"game":"24d","win":"01","timer":"90","periode":1180422}

	switch (basePcode) {
		case "m13":
			// console.log("m13 lNV", loadNewValueData);

			activeGame.win = {
				win: Number(loadNewValueData.win),
				type: loadNewValueData.type
			};
			break;

		case "m22":
		case "m23":
		case "m27":
		case "m28":
		case "m38":
			// Resets baccarat voice sets
			mainClass.baccaratVoiceSets[activeGame.pcode] = {
				lastPlayerMatch: 0,
				lastBankerMatch: 0
			};

			activeGame.pushedBigRoad = false;

			//Remove winning highlight
			delete activeGame.win;

			break;

		case "m24":
			delete activeGame.niuNiuResult;

			//winning highlight
			activeGame.win = loadNewValueData.win;
			break;

		case "m31":
			//winning highlight
			activeGame.win = loadNewValueData.win;

			window.setTimeout(() => {
				delete activeGame.lottoResult;
				if (isDesktop)
					mainClass.setState({ games: games });

				if (mobile)
					mainClass.setState({ activeGame: activeGame });
			}, 1100);
			break;

		//SamGong uses mix of roadmap & lottoResult
		case "m32":
			const winner = compareSamGongResult(loadNewValueData.angka, loadNewValueData.angka1);
			activeGame.win = winner === SamGongWinners.player ? loadNewValueData.angka : loadNewValueData.angka1;

			window.setTimeout(() => {
				activeGame.pushedBigRoad = false;
				delete activeGame.lottoResult;

				if (isDesktop)
					mainClass.setState({ games: games });

				if (mobile)
					mainClass.setState({ activeGame: activeGame });
			}, 1100);

			break;

		//Result Win GIF
		default:
			//winning highlight
			activeGame.win = loadNewValueData.win;
			break;
	}
}

/**
 * Reset bet close check, transparent chips, bet insertion, close bet SFX, stream hover, bets, total bet, bet history.
 * @param game 
 * @param loadNewValueData 
 */
function resetCommonGameProps(game: IGameType, loadNewValueData: any) {
	const basePcode = getBasePcode(game.pcode);

	game.hasBetCloseChecked = false;
	game.transparentChips = false;

	// Resets allow click submit
	game.disallowInsertionAfterClickSubmit = false;

	// Will play "no more bets" voice when the timer reaches -1
	game.hasPlayedCloseBetSFX = false;

	// Adds stream hover rerender
	game.hover = false;

	//Clears all bet
	game.bet = {};
	game.lastSubmittedTotal = 0;

	game.periode = loadNewValueData.periode;

	if (skipLoadNewValuePcodes.indexOf(basePcode) < 0)
		game.historyBlink = true;

	// Resets total bet
	recountTotal(game);

	clearBetHistory(game);
}

/**
 * Push a new array member into game.history
 * @param game Game type
 * @param loadNewValueData Arbitrary loadNewValue socket data
 */
function pushGameResultHistory(game: IGameType, loadNewValueData: any) {
	if (!game) {
		console.error("game is empty!");
		return;
	}

	const basePcode = game.pcode;
	const data = loadNewValueData;

	if (!game.history) {
		console.warn("No history prop in game" + game.pcode + ":", game);
		return;
	}

	switch (basePcode) {
		case "m14":
			game.history.push({ periode: 0, ...data.win });
			break;

		case "m16":
			data.win.periode = 0;
			game.history.push(data.win);
			break;

		case "m19":
			game.history.push({ angka: data.result, angkawin: data.win, periode: 0 });
			break;

		case "m32":
			game.history.push(data);
			break;

		case "m33":
			game.history.push({ ball: data.win, periode: data.periode });
			break;

		default:
			game.history.push({ angka: data.win, periode: 0 });
			break;
	}
}

/**
 * Reset/delete gong ball result, virtual card scan result, niu niu result.
 * @param {IGameType} game The game type, member of IGameObjectType
 */
function resetVirtualDisplays(game: IGameType) {
	const basePcode = getBasePcode(game.pcode);

	if (["m16", "m33"].indexOf(basePcode) >= 0)
		console.debug(`rVD pcode: ${game.pcode}`);

	// if (game.pcode.indexOf("m28") >= 0)
	// 	console.log("rVD I4S " + game.pcode);

	// Fill games with their default objects
	switch (basePcode) {
		case "m16":
		case "m33":
			delete game.gongBallResult;
			break;

		case "m22":
		case "m28":
		case "m38":
			delete game.virtualCardScanResult;
			break;

		case "m23":
			game.virtualCardScanResult = defaultDragonTigerResult;
			break;

		case "m24":
			// game.niuNiuResult = defaultNiuNiuResult;
			delete game.niuNiuResult;
			break;

		case "m27":
			game.virtualCardScanResult = defaultShioFightResult;
			break;
		default: break;
	}
}

function resetNewSet(game: IGameType) {
	const basePcode = game.pcode;

	// Games with New Shoe in Progress placeholder
	if (basePcode === "m22" ||
		basePcode === "m23" ||
		basePcode === "m27" ||
		basePcode === "m38") {
		game.newSet = false;
	}
}

function postLoadNewValue(mainClass: Desktop | MobileGame, game: IGameType, loadNewValueData: any) {
	// Refreshes userdata
	if (mainClass instanceof Desktop)
		mainClass.fetchUserData(null);
	else mainClass.fetchUserData(null);

	// Fetches result history
	mainClass.fetchResultHistory(null);

	if (mainClass.playGameStartSFX)
		mainClass.playGameStartSFX();

	playTTS(mainClass, game.tts, loadNewValueData.win);

	// in case the player is still in LobbyMobile
	window.setTimeout(() => {
		if (mainClass.playOpenBetSFX)
			mainClass.playOpenBetSFX();
	}, 2500);
}

//Common Gong Ball Functions
export enum GongBallSides {
	player,
	banker
}

function applyGongBallResult(game: IGameType, side: GongBallSides, socketData: string) {
	if (!game) return;

	if (!game.gongBallResult)
		game.gongBallResult = getDefaultGongBallResult();

	// console.debug("aGBR socketData", socketData);

	game.gongBallResult[GongBallSides[side]] = socketData;
}

function getGongBallTotal(gongBallResult: IGongballResult) {
	if (!gongBallResult)
		return null;

	const gongBallTotal = { player: 0, banker: 0 };

	if (gongBallResult.player)
		gongBallTotal.player = gongBallResult.player.split(",")
			.map(num => !isNaN(+num) ? Number(num) : 0)
			.reduce((x, y) => x + y) % 10;
	if (gongBallResult.banker)
		gongBallTotal.banker = gongBallResult.banker.split(",")
			.map(num => !isNaN(+num) ? Number(num) : 0)
			.reduce((x, y) => x + y) % 10;

	return gongBallTotal;
}


/**
 * Fetch result history for ResultHistoryPopup
 */
function fetchResultHistory(e: any) {
	const mobileGame = this;

	let targetPage = 1;
	if (e) {
		targetPage = e.currentTarget.getAttribute("page");
		// console.log("fRH tP", targetPage);
	}

	this.setState({
		showGameResultHistoryLoadingOverlay: true,
		gameResultHistoryPage: targetPage
	});

	const gameNameURL = replaceGameNameWithURL(
		mobileGame.state.useTempData ?
			getBasePcode(mobileGame.activePcode) :
			mobileGame.activePcode,
		mobileGame.state.games
	);

	const request = (mobileGame.state.useTempData) ?
		new Request(tempDataPage + "?page=result-history-" + gameNameURL) :
		new Request(
			"/games/list-game/" +
			gameNameURL + "/" +
			targetPage);
	// https://newlobby.menangtoto.pw/games/list-game/roulette/1

	let init: RequestInit;
	if (!mobileGame.state.useTempData)
		init = {
			credentials: "same-origin",
			method: "GET",
		};

	fetch(request, init)
		.then((res: { json: () => any; }) => res.json())
		.catch((error: any) => {
			this.setState({ showErrorPage: false, errorType: "lostauth", errorMessage: "" });
			return console.error(error);
		})
		.then((data: any) => {
			// console.log("fRH data", data);

			const activeGame = mobileGame.state.activeGame;
			activeGame.resultHistory = data;

			mobileGame.setState({
				activeGame: activeGame,
				showGameResultHistoryLoadingOverlay: false
			});
		});
}



// COMMON M26 FUNCTIONS

//BEGIN FOR m26
function bindLottoFunctions(self: any) {
	self.removeNumberM26 = removeNumberM26.bind(self);
	self.setActiveNumberM26 = setActiveNumberM26.bind(self);
	self.checkSwapNumberM26 = checkSwapNumberM26.bind(self);
}

function removeNumberM26(e: any) {
	const pcode = e.currentTarget.getAttribute("pcode");
	const idx = e.currentTarget.getAttribute("ticketindex");
	const games = this.state.games;

	console.log(pcode, idx);

	games[pcode].numbersM26.splice(idx, 1);

	this.setState({ games: games });
}

function setActiveNumberM26(e: any) {
	const pcode = e.currentTarget.getAttribute("pcode");
	const value = e.currentTarget.getAttribute("value");
	const games = this.state.games;

	if (games[pcode].numbersM26.indexOf(Number(value)) < 0 &&
		games[pcode].numbersM26.length < 9)
		games[pcode].numbersM26.push(Number(value));

	// console.log("sANM26", value);
	this.setState({ games: games, activeNumberM26: value });
}

function checkSwapNumberM26(e: any) {
	const pcode = e.currentTarget.getAttribute("pcode");
	const ticketIndex = e.currentTarget.getAttribute("ticketindex");
	let activeTicketIndexM26 = this.state.activeTicketIndexM26;
	const games = this.state.games;

	// console.log("p tI", pcode, ticketIndex);

	if (!games[pcode].numbersM26[ticketIndex])
		return;

	//performs swap
	if (activeTicketIndexM26 >= 0) {
		const old = games[pcode].numbersM26[ticketIndex];
		games[pcode].numbersM26[ticketIndex] = games[pcode].numbersM26[activeTicketIndexM26];
		games[pcode].numbersM26[activeTicketIndexM26] = old;
	}

	activeTicketIndexM26 = (activeTicketIndexM26 < 0) ? ticketIndex : -1;

	this.setState({
		games: games,
		activeTicketIndexM26: activeTicketIndexM26
	});
}

//END FOR M26

/**
 * Check if the object is either undefined, null, or an empty string.
 * @param obj 
 */
function isNullOrEmpty(obj: any) {
	return (obj === undefined || obj === null || obj === "") && obj !== 0;
}

// END OF LOBBY FUNCTIONS


// COMMON GAME FUNCTIONS

//Generic Controller interactions
//game: reference to game object
//userdata: reference to userdata state
function applyClickUndo(game: IGameType) {
	if (game.betHistory.length === 0)
		return;

	const lastBetHistory: IBetHistory = game.betHistory[game.betHistory.length - 1];

	// console.log("cU lBH", lastBetHistory);
	const basePcode = getBasePcode(game.pcode);

	if (lastBetHistory.type) {
		switch (lastBetHistory.type) {
			//m29 (Shio Balls)
			//m30 (5D)
			case "addNumberM26":
			case "addAnimal":
			case "addNumberM30":
				game
					.groupedUnsubmittedTickets
				[lastBetHistory.betGroup]
				[lastBetHistory.unsubmittedTicketIdx]
					.numbers
				[lastBetHistory.numberIdx] = null;
				break;

			case "addNumberM42":
				game
					.unsubmittedTickets
				[lastBetHistory.unsubmittedTicketIdx]
					.numbers
				[lastBetHistory.numberIdx] = null;
				break;


			case "removeNumberM26":
			case "removeAnimal":
			case "removeNumberM30":
				game
					.groupedUnsubmittedTickets
				[lastBetHistory.betGroup]
				[lastBetHistory.unsubmittedTicketIdx]
					.numbers
				[lastBetHistory.numberIdx] = lastBetHistory.number;
				break;

			case "removeNumberM42":
				game
					.unsubmittedTickets
				[lastBetHistory.unsubmittedTicketIdx]
					.numbers
				[lastBetHistory.numberIdx] = lastBetHistory.number;
				break;


			case "addBet":
				// Undo bet for machine games
				if (/(m26|m29|m30)/.test(basePcode))
					// game.pcode === "m26" ||
					// game.pcode === "m29" ||
					// game.pcode === "m30")
					game
						.groupedUnsubmittedTickets
					[lastBetHistory.betGroup]
					[lastBetHistory.unsubmittedTicketIdx]
						.bet -= lastBetHistory.amount;
				else if (basePcode === "m42")
					game
						.unsubmittedTickets
					[lastBetHistory.unsubmittedTicketIdx]
						.bet -= lastBetHistory.amount;

				recountTotal(game);
				break;

			case "removeBet":
				//Undo bet for machine games
				if (/(m26|m29|m30)/.test(basePcode))
					game
						.groupedUnsubmittedTickets
					[lastBetHistory.betGroup]
					[lastBetHistory.unsubmittedTicketIdx]
						.bet = lastBetHistory.bet;
				else if (basePcode === "m42")
					game
						.unsubmittedTickets
					[lastBetHistory.unsubmittedTicketIdx]
						.bet = lastBetHistory.bet;

				recountTotal(game);
				break;

			default: break;
		}
	} else {
		const { key } = lastBetHistory;

		if (key in game.bet) {
			game.bet[key] -= lastBetHistory.value;
			if (game.bet[key] <= 0)
				delete game.bet[key];
		}

		checkBetBounds(game, key.split("+")[1], key);

		recountTotal(game);
	}

	game.betHistory.pop();
}

//game: game object from fetchGameList()
//callback: function
//params: object
//  skipConfirm: skips confirmation dialogue

//Only call callback function when output is true
//Returns boolean
function applyClearBet(game: IGameType, callback: () => void | typeof undefined, params?: { skipConfirm: boolean }) {
	let output = false;

	const basePcode = getBasePcode(game.pcode);
	// const submitted = game.submitted;

	const skipConfirm = (params && params.skipConfirm);

	if (isMachineGame(basePcode)) {
		if (!skipConfirm)
			confirmAlert({
				title: "Clear Bets",
				message: "Are you sure to clear all unsubmitted bets?",

				buttons: [
					{
						label: "Yes",
						onClick: () => {
							game.bet = {};

							if (game.groupedUnsubmittedTickets)
								game.groupedUnsubmittedTickets = {};

							if (game.unsubmittedTickets)
								game.unsubmittedTickets = [];

							fillEmptyUnsubmittedTickets(game);

							recountTotal(game);
							clearBetHistory(game);

							if (callback)
								callback();
						}
					},
					{ label: "No", onClick: () => null }
				]
			});
	} else {
		game.bet = {};
		game.betOutOfBounds = {};

		recountTotal(game);
		clearBetHistory(game);

		output = true;
	}

	//Calls callback function if it is not undefined or null
	if (output && callback)
		callback();
}

// Clears bet and fills with current bet
function purgeApplyLastBet(game: IGameType, filteredLastBet: ILastBetArrayType[], ignoreSamePeriode: boolean = false) {
	game.bet = {};

	//Important lastBet console logs
	// if (isDev())
		console.log(
			"pALB fLB", filteredLastBet,
			`,g["${game.pcode}"]`,
			",p", game.periode,
			",p type:", typeof (game.periode)
		);

	if (filteredLastBet && filteredLastBet.length > 0)
		console.log("lB[0] p", filteredLastBet[0].periode);

	if (ignoreSamePeriode)
		console.log("pALB iSP", game.periode, filteredLastBet[0].periode);

	//Checks periode of activeGame against user's filteredLastBet
	if (filteredLastBet && filteredLastBet.length > 0) {
		if (!ignoreSamePeriode &&
			game.periode !== filteredLastBet[0].periode)
			return;

		//Applies lastBet to the game
		filteredLastBet.forEach((item: ILastBetType) => {
			const betKey = item.tebak + "+" + item.type;

			if (betKey in game.bet)
				game.bet[betKey] += Number(item.taruhan);
			else game.bet[betKey] = Number(item.taruhan);

			checkBetBounds(game, item.type, betKey);
		});
	}

	clearBetHistory(game);
}

/**
 * Clears bet history.
 * @param game Game object reference, member of IGameObject type.
 */
function clearBetHistory(game: IGameType): void {
	game.betHistory = [];
}

export type ApplyCommonInsertBetType = (chipValue: number, game: IGameType, userdata: IUserdataType, isEvent: boolean, e: any) => boolean;

// Format:
// { pcode: [ array of bet & the bet's opposing bet key ]}

const monopolyN50 = [
	["indonesia", "malaysia", "japan", "china", "macao", "india", "philippines", "laos"],
	["singapore", "brunei", "korea", "hongkong", "cambodia", "taiwan", "vietnam", "thailand"]
];

const oppositionBetList = {
	"p6": [
		["Black", "Red+n50"],
		["Red", "Black+n50"],

		["Small", "Big+n50"],
		["Big", "Small+n50"],

		["Odd", "Even+n50"],
		["Even", "Odd+n50"]
	],
	"p7": [
		["1 to 18", "19 to 36+n50"],
		["19 to 36", "1 to 18+n50"],

		["red", "black+n50"],
		["black", "red+n50"],

		["odd", "even+n50"],
		["even", "odd+n50"]
	],

	"m10": [
		["d1", "d2+d"],
		["d2", "d1+d"],

		["d3", "d4+d"],
		["d4", "d3+d"],

		["d5", "d6+d"],
		["d6", "d5+d"],

		["head", "tail+n50"],
		["tail", "head+n50"],

		["left", "right+n50"],
		["right", "left+n50"]
	],

	"m16": [
		["small", "big+n50"],
		["big", "small+n50"],

		["odd", "even+n50"],
		["even", "odd+n50"],

		["Player", "Banker+player"],
		["Banker", "Player+player"]
	],

	"m20": [


		//Mobile portrait
		[monopolyN50[0].join(","), `${monopolyN50[1].join(",")}+n50`],
		[monopolyN50[1].join(","), `${monopolyN50[0].join(",")}+n50`]
	],

	"m22": [
		["player", "banker+banker"],
		["banker", "player+player"],

		["small", "big+big"],
		["big", "small+small"]
	],
	"m23": [
		["dragon", "tiger+tiger"],
		["tiger", "dragon+dragon"],

		["dragon-black", "dragon-red+n50"],
		["dragon-red", "dragon-black+n50"],

		["dragon-big", "dragon-small+n50"],
		["dragon-small", "dragon-big+n50"],

		["dragon-odd", "dragon-even+n50"],
		["dragon-even", "dragon-odd+n50"],

		["tiger-black", "tiger-red+n50"],
		["tiger-red", "tiger-black+n50"],

		["tiger-big", "tiger-small+n50"],
		["tiger-small", "tiger-big+n50"],

		["tiger-odd", "tiger-even+n50"],
		["tiger-even", "tiger-odd+n50"]
	],

	"m25": [
		["odd", "even+n50"],
		["even", "odd+n50"]
	],

	"m27": [
		["di", "tian+tian"],
		["tian", "di+di"]
	],

	"m30": [
		["big", "small+n50"],
		["small", "big+n50"],

		["odd", "even+n50"],
		["even", "odd+n50"],

		["all-odd", "all-even+n50"],
		["all-even", "all-odd+n50"],

		["all-low", "all-high+n50"],
		["all-high", "all-low+n50"]
	],
	"m32": [
		["player", "banker+banker"],
		["banker", "player+player"],

		["player-odd", "player-even+n50"],
		["player-even", "player-odd+n50"],

		["player-big", "player-small+n50"],
		["player-small", "player-big+n50"],

		["banker-odd", "banker-even+n50"],
		["banker-even", "banker-odd+n50"],

		["banker-big", "banker-small+n50"],
		["banker-small", "banker-big+n50"]
	],

	"m35": [
		["Small", "Big+n50"],
		["Big", "Small+n50"],

		["Red", "Black+n50"],
		["Black", "Red+n50"],

		["Odd", "Even+n50"],
		["Even", "Odd+n50"]
	],

	"m36": [
		["small", "big+n50"],
		["big", "small+n50"],

		["red", "black+n50"],
		["black", "red+n50"],

		["odd", "even+n50"],
		["even", "odd+n50"]
	],

	"m39": [
		["Red", "Blue+n50"],
		["Blue", "Red+n50"],

		["Odd Red", "Even Red+n50"],
		["Even Red", "Odd Red+n50"],

		["Low Red", "High Red+n50"],
		["High Red", "Low Red+n50"],

		["Odd Blue", "Even Blue+n50"],
		["Even Blue", "Odd Blue+n50"],

		["Low Blue", "High Blue+n50"],
		["High Blue", "Low Blue+n50"],
	]
};

//24D
// oppositionBetList["m6"] = oppositionBetList["p6"];

[
	"m6", "m8", "m13", "m14",
	"p9",
	"p12", "m31", "m37" //Sicbo ball & machine
].forEach(pcode => {
	oppositionBetList[pcode] = oppositionBetList["p6"]
});

oppositionBetList["m28"] = oppositionBetList["m22"]
oppositionBetList["m38"] = oppositionBetList["m22"]

//New Function
function applyCommonInsertBet(chipValue: number, game: IGameType, userdata: IUserdataType, isEvent: boolean, e: any): boolean {
	// const forced = e.forced || false;

	const bet = !isEvent ? e.bet : e.currentTarget.getAttribute("bet");
	const group = !isEvent ? e.group : e.currentTarget.getAttribute("group");

	const basePcode = getBasePcode(game.pcode);

	// Cancel out the opposing bet insertion
	// (Red vs Black, if red has 5000 value, then the player can't insert bet on black)
	if (basePcode in oppositionBetList) {
		// console.log("bet, group:", `"${bet}"`, `"${group}"`);

		const oppositeBetAry = oppositionBetList[basePcode].filter((item: string) =>
			item[0] === bet
		);

		// console.log("opposite", oppositeBetAry);

		if (oppositeBetAry.length > 0 &&
			game.bet[oppositeBetAry[0][1]] > 0)
			return false;
	}


	//Counts remainder when the placed bet overflows user balance
	const userBalance = userdata.user.balance;
	const lowestChipValue = Math.min(...userdata.user.chipBase);

	//Remainder: the trimmed chip value

	//For Example:
	// Balance: 20100
	// Inserted chip value: 50000

	// Remainder: abs(20100 - 50000) = 39900

	let remainder = 0;

	const newTotalBet = game.lastSubmittedTotal ?
		game.totalBet - game.lastSubmittedTotal :
		game.totalBet;

	// Don't insert chip on Set if user balance isn't sufficient
	if (game.pcode === "m26" || game.pcode === "m30") {
		const groupAttr = !isEvent
			? e.group
			: e.currentTarget.getAttribute("group");

		//console.log("machineGame insert bet",
		//	group,
		//	newTotalBet,
		//	chipValue,
		//	userBalance
		//);

		if ((groupAttr.indexOf("set") >= 0) &&
			(newTotalBet + (chipValue * setMultipliers[game.pcode]) > userBalance))
			return false;
	}

	if (newTotalBet + chipValue > userBalance) {
		remainder = newTotalBet + chipValue - userBalance;

		if (remainder === chipValue)
			return false;

		chipValue -= remainder;
	}


	//Custom insertBet for m29 (skips Rebet)
	if (!bet) {
		if (isMachineGame(game.pcode)) {
			const unsubmittedTicketIdx = Number(e.currentTarget.getAttribute("index"));

			if (game.unsubmittedTickets) {
				const ticket = game.unsubmittedTickets[unsubmittedTicketIdx];

				// Prevent bet insertion when the chipValue remainder is less than lowest chip value
				if ((ticket.bet || 0) + chipValue < lowestChipValue)
					return false;

				ticket.bet += chipValue;

				game.betHistory.push({
					type: "addBet",
					unsubmittedTicketIdx,
					amount: chipValue
				});
			} else {
				const ticket = game.groupedUnsubmittedTickets[group][unsubmittedTicketIdx];

				// Prevent bet insertion when the chipValue remainder is less than lowest chip value
				if ((ticket.bet || 0) + chipValue < lowestChipValue)
					return false;

				ticket.bet += chipValue;

				game.betHistory.push({
					type: "addBet",
					unsubmittedTicketIdx: unsubmittedTicketIdx,
					betGroup: group,
					amount: chipValue
				});
			}
		}
	} else {
		const key = bet + "+" + group;

		//Prevents bet insertion when the chipValue remainder is less than lowest chip value
		if ((game.bet[key] || 0) + chipValue < lowestChipValue)
			return false;

		if (key in game.bet)
			game.bet[key] += chipValue;
		else game.bet[key] = chipValue;

		//Checks bet bounds (min/max/min50/max50)
		checkBetBounds(game, group, key);

		//adds a bet to betHistory
		game.betHistory.push({
			key: key,
			value: chipValue
		});
	}

	recountTotal(game);

	return true;
}


export interface IFetchPostDataType {
	button_bet: {
		button: string,
		group: string,

		value: number
	}[];

	total_bet: number;
	game: string;
	transId: string;
}

//Summary: Converts game bet into Object that is appropriate to submit
//Parameters:
// game: individual game object from fetchGameList()
// fetchPostData: Object reference to what will be submitted on clickSubmit()
function applyCommonSubmitBet(game: IGameType, fetchPostData: IFetchPostDataType) {
	const basePcode = getBasePcode(game.pcode);

	//Processes and validates Submit bet for SB
	switch (basePcode) {
		//MACHINE GAMES
		//Race Ball
		case "m26":
			//Process button_bet and validation
			applyM26SubmitTickets(game, fetchPostData);

			//Allows user to add another ticket
			game.mustSubmitTickets = false;

			break;

		//Shio Balls
		case "m29":
			//Same as m26, with slight modifications

			//Process button_bet and validation
			applyM29SubmitTickets(game, fetchPostData);

			//Allows user to add another ticket
			game.mustSubmitTickets = false;
			break;

		//5D
		case "m30":
			//Process button_bet and validation
			applyM30SubmitTickets(game, fetchPostData);

			//Allows user to add another ticket
			game.mustSubmitTickets = false;
			break;

		case "m42":
			applyM42SubmitTickets(game as IGameTypeM42, fetchPostData);
			game.mustSubmitTickets = false;
		break;

		//END OF MACHINE GAMES

		default:
			//Default bet accumulation to submit
			for (const a in game.betHistory) {
				const splitt = game.betHistory[a].key.split("+");
				// "12+n"
				fetchPostData.button_bet.push({
					button: splitt[0],
					group: splitt[1],

					value: game.betHistory[a].value
				});

				fetchPostData.total_bet += game.betHistory[a].value;
			}

			break;
	}

	// Clear bets for machine game
	if (isMachineGame(game.pcode))
		game.bet = {};
}

//Summary: Remove invalid bets by min-max
//Parameters:
// game: individual game object from fetchGameList()
// userdata: the user data object from fetchUserData()
function sanitiseBets(game: IGameType) { //, userdata
	for (const a in game.bet) {
		const splitt = a.split("+");

		const is50 = ary50.indexOf(splitt[1]) >= 0;

		//Apply max50 or just max according to the group
		// if the bet overflows the limit

		if (is50 && game.bet[a] > game.max50)
			game.bet[a] = game.max50;
		else if (!is50 && game.bet[a] > game.max)
			game.bet[a] = game.max;
	}
}


function restoreChipsFromSamePeriod(mainClass: Desktop | MobileGame) {
	const {
		games,
		userdata
	} = mainClass.state;

	if (!games)
		return;

	Object.keys(games).forEach(pcode => {
		const lastBetArray = getLastBetArray(userdata, pcode);

		if (lastBetArray.length === 0)
			return;

		if (lastBetArray[0].periode ===
			games[pcode].periode) {
			const clickRebetParam = {
				pcode: pcode,
				forced: true,
				noSFX: true,
				submitted: true
			};
			if (mainClass instanceof Desktop)
				mainClass.clickRebet(clickRebetParam);
			else mainClass.clickRebet(clickRebetParam);
		}
	});
}

//Generic Controller checks
export type CheckControllerType = typeof checkController;

function checkController(game: IGameType): void {
	const pcode = game.pcode;

	let skipTotalBetCheck = false;

	game.disableUndoBet = false;
	game.disableClearBet = false;
	game.disableRebet = false;
	game.disableSubmitBet = false;

	//Checks for last bet object in userdata
	if (this.state.userdata &&
		this.state.userdata.user.lastBet) {
		const lastBetArray = getLastBetArray(this.state.userdata, pcode);

		if (lastBetArray.length === 0) {
			game.disableRebet = true;
			skipTotalBetCheck = true;
		}
	}

	//Checks for betHistory
	const hasEmptyBetHistory = (game.betHistory.length === 0);

	game.disableUndoBet = hasEmptyBetHistory;
	game.disableClearBet = hasEmptyBetHistory;

	//Checks for totalBet in gameObject
	if (!skipTotalBetCheck)
		game.disableRebet = game.totalBet > 0;

	//Checks for submit bet button

	//First totalBet check
	game.disableSubmitBet = game.totalBet === 0;

	if (game.totalBet > 0 && game.submitted)
		game.disableSubmitBet = true;

	game.disableSubmitBet = hasEmptyBetHistory;



	//Checks Controller for Race Ball & SB
	if (isMachineGame(pcode)) {
		//Checks Rebet Button
		game.disableRebet = (game.tickets && game.tickets.length > 0) ||
			(!game.lastPeriodTickets || game.lastPeriodTickets.length === 0) ||
			game.mustSubmitTickets ||
			!hasEmptyBetHistory;

		//Checks Submit Button
		if (game.mustSubmitTickets)
			game.disableSubmitBet = (
				game.tickets &&
				game.tickets.length === 0
			);
		else game.disableSubmitBet = hasEmptyBetHistory || game.totalBet === 0;
	}


	//Checks timer
	if (this.state.timers) {
		const timeObject = this.state.timers[pcode];

		if (timeObject && isBetClosed(timeObject.time)) {
			game.disableUndoBet = true;
			game.disableClearBet = true;
			game.disableRebet = true;
			game.disableSubmitBet = true;
		}
	}
}

//Counts the total bet
const setMultipliers: IKeyNumberDict = {
	m29: 3,
	m30: 5
};

//How total bet works:
//- other games:
//	  All Bets
//- for machine game:
//	  Running Tickets + All Unsubmitted Tickets
function recountTotal(game: IGameType) {
	let betTotal = 0, //50-50 and any other bets in game.bet

		ticketTotal = 0, //bets in game.tickets
		unsubmittedTicketTotal = 0; //bets in game.unsubmittedTickets

	//Generic bets like V1 games & also 5D 50-50 bets
	if (game.bet)
		for (const a in game.bet)
			betTotal += game.bet[a];

	//Count unsubmitted ticket total
	if (isMachineGame(game.pcode)) {
		const basePcode = getBasePcode(game.pcode);

		const { groupedUnsubmittedTickets } = game;

		if (basePcode === "m42") {
			for (const ticket of (game as IGameTypeM42).unsubmittedTickets)
				unsubmittedTicketTotal += ticket.bet;
		} else if (game.groupedUnsubmittedTickets) {
			// Race Ball & 5D, copied from below
			Object.keys(groupedUnsubmittedTickets).forEach((ticketGroup: string) => {
				groupedUnsubmittedTickets[ticketGroup].forEach((ticket: { bet: number; }) => {
					//Only for machine game sets
					if (ticketGroup.indexOf("set") >= 0)
						unsubmittedTicketTotal += ticket.bet * setMultipliers[game.pcode];
					else unsubmittedTicketTotal += ticket.bet;
				});
			});
		}
	}

	//Sum all running tickets
	ticketTotal = getTicketTotalBet(game);

	game.totalBet = ticketTotal + betTotal + unsubmittedTicketTotal;
}

//Counts total bet in tickets
function getTicketTotalBet(game: IGameType) {
	let total = 0;

	if (game.tickets && game.tickets.length > 0)
		game.tickets.forEach((ticketItem: { taruhan: number; }) => {
			total += ticketItem.taruhan;
		});

	return total;
}

function hasBets(game: IGameType) {
	for (const key in Object.keys(game.bet))
		if (game.bet[key] > 0)
			return true;
	return false;
}

// END OF COMMON GAME FUNCTIONS

function getCSSSize(size: string) {
	const num = parseFloat(size);
	const unit = size.replace(num + "", "").trim();
	return [num, unit];
}

//to help with user behaviour testing
function allowObserverScripts(userdata: IUserdataType) {
	return userdata.user.operatorId === "891623504";
}

function activateSmartlookClient() {
	smartlookClient.init("05549bd9d30d41794475b149884405d1233d15ec");
}

function activateFullStoryObserver(userdata: IUserdataType) {
	identify(userdata.user.uid + "", { test: "Test" });
}


/** 
 * check if file exist
 */
function VideoTutorialSource(pcode: string) {
	/**
	 * TODO: video berdasrakan bahasa
	 */
	const urlToFile = "/mp4/tutorials/" + pcode + "/id.mp4";

	const xhr = new XMLHttpRequest();
	xhr.open('GET', urlToFile, false);
	xhr.send();

	const type = xhr.getResponseHeader("Content-Type");
	const src = (type !== "video/mp4") ? null : urlToFile;

	return src;
}

function getRadians(degrees: number) {
	return degrees / 180 * Math.PI;
}

export {
	isDev, devWarnings,
	VideoTutorialSource,

	defaultLogo,

	chipColours,

	showTypeResultHistory,

	isMobile,
	timeLeftStrToto,


	//Consts
	historyOnlyGames, roadmapOnlyGames, bigChipGames,
	isHistoryOnlyGame, isBigChipGame,

	skipLoadNewValuePcodes,
	getDefaultGongBallResult,
	rouletteReds, twelveDReds, twentyFourDReds, fortyEightDReds, headTailLefts, monopolyCircles, monopolySquares, shioFightsAnimals,

	//String operations
	regexIndexOf,

	//Checks for bet bounds
	checkAllBetBounds, checkBetBounds, // checkInsufficientBalance,

	// checkUnsubmittedTicketBetBounds,
	getLastBetArray, getTimeText, isBetClosed,

	// Filename-related functions
	getDefaultGameName,
	getTranslatedGameName,
	replaceGameNameWithURL,

	getWinClassName,

	// IFrame checker
	getWindowOpener,

	checkFetchStatus,

	// Component Imports
	Table, TotoTable, RoadmapLarge, RoadmapSmall, History, GameStatistics, PopupResultHistory,

	// Bet button templates
	StandardBetSB,

	//Scroll buttons
	scrollStart, scrollEnd,

	//OS-related functions
	isChrome, isChromeiOS, isElement, isFirefox, inIframe, isLandscape, isPortrait,

	maxScrollLeft,

	trimLastSlash,

	// COMMON LOBBY FUNCTIONS
	fillRoadmapText, sortNewGames, toggleFullscreen, fetchGameHistory,

	// Generic Popup Functions
	setActiveGame,

	// Load New Value Functions
	processLoadNewValueWin, resetCommonGameProps, pushGameResultHistory, resetVirtualDisplays, resetNewSet, postLoadNewValue,

	// Common Gong Ball Functions
	applyGongBallResult, getGongBallTotal,

	// COMMON TRANSACTION AND RESULT HISTORY FUNCTIONS
	fetchResultHistory,

	initUserdata, getNickname, betGroupProp,

	// COMMON M26 FUNCTIONS
	bindLottoFunctions, removeNumberM26, setActiveNumberM26, checkSwapNumberM26,

	isNullOrEmpty,

	// COMMON GAME FUNCTIONS
	applyClickUndo, applyClearBet, purgeApplyLastBet, applyCommonInsertBet, applyCommonSubmitBet, sanitiseBets, checkController, clearBetHistory, recountTotal, getTicketTotalBet, hasBets,

	restoreChipsFromSamePeriod,

	getCSSSize,

	allowObserverScripts, activateSmartlookClient, activateFullStoryObserver,

	getRadians
};
